'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import MorphySvg from './MorphySvg';
const DRAG_THRESHOLD_PX = 5;
const SAMPLE_POINTS: Array<[number, number]> = [[0.5, 0.5], [0.3, 0.35], [0.7, 0.35], [0.3, 0.65], [0.7, 0.65]];
const parseColor = (str: string): {
  r: number;
  g: number;
  b: number;
  a: number;
} | null => {
  const m = str.match(/^rgba?\(([^)]+)\)$/u);
  if (!m) return null;
  const parts = m[1].split(',').map(s => parseFloat(s.trim()));
  if (parts.length < 3 || parts.some(n => Number.isNaN(n))) return null;
  return {
    r: parts[0] / 255,
    g: parts[1] / 255,
    b: parts[2] / 255,
    a: parts[3] !== undefined ? parts[3] : 1
  };
};
const perceivedLuminance = (c: {
  r: number;
  g: number;
  b: number;
}): number => 0.299 * c.r + 0.587 * c.g + 0.114 * c.b;
const CHATWOOT_BASE_URL = 'https://support.hotdeskplex.com';
const CHATWOOT_WEBSITE_TOKEN = 'YhstuBTkbrN3oboiei8ho378';
let chatwootReady: Promise<void> | null = null;

/**
 * The Chatwoot SDK pulls ~900 KB (Vue app, CSS, API calls), so it is only
 * injected on the first press of the chat button rather than on every page
 * load. Resolves once `window.$chatwoot` is usable; a failed load resets so
 * the next press retries.
 */
const loadChatwoot = (): Promise<void> => {
  if (window.$chatwoot) return Promise.resolve();
  if (chatwootReady) return chatwootReady;
  chatwootReady = new Promise<void>((resolve, reject) => {
    window.chatwootSettings = {
      position: 'right',
      type: 'expanded_bubble',
      launcherTitle: 'Chat with us',
      hideMessageBubble: true
    };
    window.addEventListener('chatwoot:ready', () => resolve(), {
      once: true
    });
    const script = document.createElement('script');
    script.src = `${CHATWOOT_BASE_URL}/packs/js/sdk.js`;
    script.async = true;
    const fail = () => {
      script.remove();
      chatwootReady = null;
      reject(new Error('Chatwoot SDK failed to load'));
    };
    script.onload = () => {
      // Without the SDK global, `chatwoot:ready` never fires; fail so
      // the next press retries instead of waiting forever.
      if (!window.chatwootSDK) return fail();
      window.chatwootSDK.run({
        websiteToken: CHATWOOT_WEBSITE_TOKEN,
        baseUrl: CHATWOOT_BASE_URL
      });
    };
    script.onerror = fail;
    document.head.appendChild(script);
  });
  return chatwootReady;
};
const ChatwootWidget = () => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isOnDark, setIsOnDark] = useState(false);
  const dragStateRef = useRef({
    active: false,
    startPointerX: 0,
    startPointerY: 0,
    startBtnX: 0,
    startBtnY: 0,
    maxMoved: 0
  });
  const rafIdRef = useRef<number | null>(null);
  const scheduleBgSample = useCallback(() => {
    if (rafIdRef.current !== null) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      const btn = buttonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      let total = 0;
      let count = 0;
      for (const [fx, fy] of SAMPLE_POINTS) {
        const x = rect.left + rect.width * fx;
        const y = rect.top + rect.height * fy;
        const stack = document.elementsFromPoint(x, y);
        for (const el of stack) {
          if (btn.contains(el)) continue;
          const bg = window.getComputedStyle(el).backgroundColor;
          const parsed = parseColor(bg);
          if (parsed && parsed.a > 0.5) {
            total += perceivedLuminance(parsed);
            count++;
            break;
          }
        }
      }
      setIsOnDark((count > 0 ? total / count : 1) < 0.5);
    });
  }, []);
  useEffect(() => {
    scheduleBgSample();
    window.addEventListener('scroll', scheduleBgSample, {
      passive: true
    });
    window.addEventListener('resize', scheduleBgSample);
    return () => {
      window.removeEventListener('scroll', scheduleBgSample);
      window.removeEventListener('resize', scheduleBgSample);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [scheduleBgSample]);
  useEffect(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const handleResize = () => {
      setPosition(prev => {
        if (!prev) return prev;
        const maxX = window.innerWidth - btn.offsetWidth;
        const maxY = window.innerHeight - btn.offsetHeight;
        return {
          x: Math.min(Math.max(0, prev.x), maxX),
          y: Math.min(Math.max(0, prev.y), maxY)
        };
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    // Start fetching the SDK on press so it is (nearly) ready by release.
    loadChatwoot().catch(() => {});
    const btn = e.currentTarget;
    const rect = btn.getBoundingClientRect();
    dragStateRef.current = {
      active: true,
      startPointerX: e.clientX,
      startPointerY: e.clientY,
      startBtnX: rect.left,
      startBtnY: rect.top,
      maxMoved: 0
    };
    btn.setPointerCapture(e.pointerId);
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const state = dragStateRef.current;
    if (!state.active) return;
    const dx = e.clientX - state.startPointerX;
    const dy = e.clientY - state.startPointerY;
    const moved = Math.hypot(dx, dy);
    if (moved > state.maxMoved) state.maxMoved = moved;
    if (moved < DRAG_THRESHOLD_PX) return;
    const btn = e.currentTarget;
    const maxX = window.innerWidth - btn.offsetWidth;
    const maxY = window.innerHeight - btn.offsetHeight;
    setPosition({
      x: Math.min(Math.max(0, state.startBtnX + dx), maxX),
      y: Math.min(Math.max(0, state.startBtnY + dy), maxY)
    });
    scheduleBgSample();
  };
  const endDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    const state = dragStateRef.current;
    if (!state.active) return;
    state.active = false;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (state.maxMoved < DRAG_THRESHOLD_PX) {
      // Already loaded: toggle as before. First press: open once ready.
      if (window.$chatwoot) {
        window.$chatwoot.toggle();
      } else {
        loadChatwoot().then(() => window.$chatwoot?.toggle('open')).catch(() => {});
      }
    }
  };
  const positionStyle: React.CSSProperties = position ? {
    left: `${position.x}px`,
    top: `${position.y}px`,
    right: 'auto',
    bottom: 'auto'
  } : {};
  return <button ref={buttonRef} type="button" aria-label="Chat with us" className={`morphy-chat-button${isOnDark ? ' is-on-dark' : ''}`} style={positionStyle} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={endDrag} onPointerCancel={endDrag} data-sentry-component="ChatwootWidget" data-sentry-source-file="ChatwootWidget.tsx">
            <MorphySvg data-sentry-element="MorphySvg" data-sentry-source-file="ChatwootWidget.tsx" />
        </button>;
};
export default ChatwootWidget;